import {
  React, useState, useEffect,
  Box, Button, CircularProgress, Stack, Typography, TextField,
  Paper, Divider, Alert,
  authFetch, authFetchRequest,
} from './SettingsCommon';
import { getCookie } from '../utils/cookies';
import { alertC } from '../components/dialogService';

// Keys that get a password-style input
const PASSWORD_KEYS = ['smtp_password'];
// Keys that get a multiline textarea
const MULTILINE_KEYS = ['default_message'];
// Keys that are numeric
const NUMERIC_KEYS = ['smtp_port'];

// dealerId (optional): when set, the form edits that dealer's mailer settings
// instead of the owner-level ones, and an "Override owner's mailer settings"
// checkbox is shown at the top.
const MailerSettingsTab = ({ dealerId, dealerName }) => {
  const [settings, setSettings]   = useState([]);   // [{ key, value, label, description }]
  const [locals, setLocals]       = useState({});   // { key: value } — edited locally
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting]     = useState(false);

  const dealerQs = dealerId ? `?dealer_id=${dealerId}` : '';
  const isDealerScope = Boolean(dealerId);
  const overrideOn = String(locals.mail_override ?? '0') === '1';

  useEffect(() => {
    setLoading(true);
    authFetch(`/email-settings/${dealerQs}`)
      .then(data => {
        setSettings(data || []);
        const map = {};
        (data || []).forEach(s => { map[s.key] = s.value; });
        setLocals(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealerId]);

  const handleChange = (key, value) => {
    setLocals(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const s of settings) {
        if (locals[s.key] !== s.value) {
          await authFetchRequest(
            `/email-settings/${s.key}${dealerQs}`,
            { value: locals[s.key] },
            'PUT',
            { Authorization: `Bearer ${getCookie('access_token')}` }
          );
        }
      }
      // Update local baseline so next save detects changes correctly
      setSettings(prev => prev.map(s => ({ ...s, value: locals[s.key] ?? s.value })));
      await alertC('Mailer settings saved.');
    } catch (err) {
      await alertC('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      await alertC('Enter a valid recipient email.');
      return;
    }
    setTesting(true);
    try {
      const res = await authFetchRequest(
        `/email-settings/test${dealerQs}`,
        { to: testEmail },
        'POST',
        { Authorization: `Bearer ${getCookie('access_token')}` }
      );
      if (res?.message) {
        await alertC('Test email sent successfully to ' + testEmail);
      } else {
        await alertC('Failed: ' + (res?.error || 'Unknown error'));
      }
    } catch (err) {
      await alertC('Failed to send test email: ' + err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <CircularProgress size={24} sx={{ m: 3 }} />;

  // Split into SMTP config vs. email defaults
  const smtpKeys    = ['smtp_server', 'smtp_port', 'smtp_login', 'smtp_password', 'from_address'];
  const defaultKeys = ['default_subject', 'default_message'];
  const smtpSettings    = settings.filter(s => smtpKeys.includes(s.key));
  const defaultSettings = settings.filter(s => defaultKeys.includes(s.key));

  const fieldsDisabled = isDealerScope && !overrideOn;

  const renderField = (s) => (
    <Box key={s.key}>
      <TextField
        label={s.label}
        helperText={s.description}
        value={locals[s.key] ?? ''}
        onChange={e => handleChange(s.key, e.target.value)}
        type={PASSWORD_KEYS.includes(s.key) ? 'password' : 'text'}
        multiline={MULTILINE_KEYS.includes(s.key)}
        rows={MULTILINE_KEYS.includes(s.key) ? 4 : undefined}
        inputProps={NUMERIC_KEYS.includes(s.key) ? { inputMode: 'numeric', pattern: '[0-9]*' } : {}}
        size="small"
        fullWidth
        disabled={fieldsDisabled}
        sx={{ mb: 2 }}
      />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
        {isDealerScope
          ? `Dealer ${dealerName || ''} — Mailer Settings`
          : 'Mailer Settings'}
      </Typography>

      {/* Override toggle (dealer scope only) */}
      {isDealerScope && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: '8px', borderLeft: '4px solid #ed6c02' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={overrideOn}
              onChange={e => handleChange('mail_override', e.target.checked ? '1' : '0')}
              style={{ width: 16, height: 16 }}
            />
            <span style={{ fontWeight: 600 }}>Override owner's mailer settings</span>
          </label>
          <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
            {overrideOn
              ? 'This dealer uses its own SMTP server and email defaults below.'
              : 'This dealer inherits all mailer settings from the owner.'}
          </Typography>
        </Paper>
      )}

      {/* SMTP Configuration */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: '8px', borderLeft: '4px solid #1976d2' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          SMTP Configuration
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Configure the outgoing mail server. Any SMTP provider (Gmail, Outlook, Brevo, etc.) is supported.
        </Typography>
        {smtpSettings.map(renderField)}
      </Paper>

      {/* Default Email Content */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: '8px' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Default Email Content
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Pre-filled subject and message shown when a user clicks "Send Email" on a quote.
        </Typography>
        {defaultSettings.map(renderField)}
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          size="small"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* Test Email */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: '8px' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Send Test Email
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Verify your SMTP settings are working by sending a test message.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Recipient email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            size="small"
            sx={{ width: 280 }}
          />
          <Button
            variant="outlined"
            onClick={handleTest}
            disabled={testing || (isDealerScope && !overrideOn)}
            size="small"
            sx={{ mt: 0.5 }}
          >
            {testing ? 'Sending...' : 'Send Test'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default MailerSettingsTab;
